import { Col, Row } from 'antd';
import { useEffect } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';

import { RootState } from '../../../state/store';
import { Breadcrumb } from '../../AppHeader/interfaces';
import { getCurrentTheme } from '../../../helpers/theme';
import { transferStatuses } from '../../../config/commons';
import { formatLongDateWithOffset } from '../../../utils/date';
import { LrvTag } from '../../../common/components/LrvTag/LrvTag';
import IconButton from '../../../common/components/buttons/IconButton';
import { LrvTable } from '../../../common/components/LrvTable/LrvTable';
import Breadcrumbs from '../../../common/components/Breadcrumb/Breadcrumbs';
import { getTitleByStockingPhaseType } from '../../../helpers/stocking.helpers';
import { changeBreadcrumb, changeHeader, fetchCompany, setPhaseType } from '../../AppHeader/headerSlice';

import styles from './WeighingTransfers.module.scss';
import * as weighingTransfersSlice from './weighingTransfersSlice';
import { WeighingTransfer as IWeighingTransfer } from './interfaces';

type TParams = { stockingId: string };

export const WeighingTransfers = ({ match }: RouteComponentProps<TParams>) => {
  const [t] = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = getCurrentTheme();

  const stockingId = match.params.stockingId;

  const { selectedStocking, transfers, isDownloadingTransferSummaryPdf } = useSelector((state: RootState) => state.weighingTransfers);
  const { breadcrumb, company } = useSelector((state: RootState) => state.header);
  const { _id: companyId } = company;

  /*useEffect section*/
  useEffect(() => {
    dispatch(changeHeader({ title: 'stockings.weighingTransfers' }));
  }, [dispatch]);

  useEffect(() => {
    if (!selectedStocking?.name) {
      return;
    }

    const breadcrumb: Breadcrumb[] = [{
      id: 'sowings',
      name: getTitleByStockingPhaseType(selectedStocking.phaseType),
      url: '/production/stockings',
    },
    {
      id: selectedStocking.name,
      name: `${t('stockings.stocking')} ${selectedStocking.name}`,
      url: `/production/stockings/${stockingId}`,
    },
    {
      id: 'weighing-transfers',
      name: t('stockings.weighingTransfers'),
      url: `/production/stockings/${stockingId}/weighing-transfers`,
    }];

    dispatch(changeBreadcrumb(breadcrumb));
    dispatch(setPhaseType(selectedStocking.phaseType));
  }, [dispatch, stockingId, selectedStocking?.name, selectedStocking?.phaseType]);

  useEffect(() => {
    dispatch(weighingTransfersSlice.fetchStocking({ stockingId }));
  }, [dispatch, stockingId]);

  useEffect(() => {
    if (companyId || !selectedStocking?._id) {
      return;
    }

    dispatch(fetchCompany(selectedStocking.companyId));
  }, [dispatch, companyId, selectedStocking?.companyId]);


  useEffect(() => {
    if (!companyId) {
      return;
    }

    dispatch(weighingTransfersSlice.fetchWeighingTransfers({ stockingId, companyId }));
  }, [dispatch, stockingId, companyId]);
  /*End of useEffect section*/

  /*render components section */
  const renderButtonGeneratePdf = () => {
    if (!selectedStocking?.name || !transfers?.length) {
      return null;
    }

    return (
      <IconButton
        className={styles.generatePdf}
        onClick={() => {
          if (!selectedStocking?.name) {
            return;
          }

          dispatch(weighingTransfersSlice.downloadTransferSummaryPdf({ stockingId, stockingName: selectedStocking?.name, companyId }));
        }}
        iconName='download'
        loading={isDownloadingTransferSummaryPdf}
        placement='left'
        tooltipText={t('survivalRate.generate')}
      />
    );
  };

  const renderSubHeader = () => {
    if (!selectedStocking?._id) {
      return null;
    }

    return (
      <Row className={styles.rowHeader} align='middle' justify='space-between'>
        <Breadcrumbs breadcrumb={breadcrumb} noCapitalize theme={theme} />
        {renderButtonGeneratePdf()}
      </Row>
    );
  };

  const renderStatusTag = (status: string) => {
    const active = status === transferStatuses.ACTIVE;

    if (active) {
      return <LrvTag type='active' theme={theme}>{t('stockings.transfers.status.active').toLowerCase()}</LrvTag>;
    }

    return <LrvTag theme={theme}>{t('stockings.transfers.status.completed').toLowerCase()}</LrvTag>;
  };

  const columns: ColumnsType<IWeighingTransfer> = [
    {
      key: 1,
      width: '18%',
      dataIndex: 'name',
      title: t('stockings.transfers.name'),
    },
    {
      key: 2,
      width: '15%',
      dataIndex: 'transferDate',
      title: t('stockings.transfers.transferDate'),
      render: (value) => formatLongDateWithOffset(value),
    },
    {
      key: 3,
      width: '10%',
      dataIndex: 'status',
      title: '',
      render: (value) => {
        return renderStatusTag(value);
      }
    },
  ];
  /* End of render components section */

  return (
    <div className={styles.weighingTransfers}>
      <Row className={styles.rowFlex}>
        <Col className={styles.columnFlex} >
          {renderSubHeader()}
          <LrvTable
            size='small'
            theme={theme}
            columns={columns}
            pagination={false}
            dataSource={transfers}
            scroll={{ y: '' }}
            onRow={(record: IWeighingTransfer) => {
              return {
                onClick: (e) => {
                  if (record.status === transferStatuses.ACTIVE) {
                    return;
                  }

                  e.stopPropagation(); // This avoids click confusion when extraAction buttons are clicked
                  history.push(`/production/stockings/${stockingId}/weighing-transfers/${record._id}`);
                }
              };
            }}
          />
        </Col>
      </Row>
    </div>
  );
};
